// CaseTableDockerSideBar.jsx
// Display the sidebar of the case table with the slide folder and case information as dockable tabs

// Copyright HS Analysis GmbH, 2023
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports
import { makeStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";

// Icon imports
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import HSAIcon from "../../common/utils/HSAIcon";

// External imports
import DockLayout from "rc-dock";

// HSA imports
import FilePreviewList from "../../fileViewer/components/FilePreviewList";
import CaseInformation from "./CaseInformation";
import { SortByType } from "../../fileViewer/FileViewer";
import FileFormatUtils from "../../common/utils/FileFormatUtils";
import FilePreviewEditDialog from "../../fileViewer/components/FilePreviewEditDialog";

export const CaseContext = React.createContext();
export default function CaseTableDockSideBar(props) {
  const { selCase } = props;
  const classes = useStyles();
  const [anchorElFolder, setAnchorElFolder] = React.useState(null);
  const [anchorElPreview, setAnchorElPreview] = React.useState(null);
  const [dockKey, setDockKey] = React.useState(0);
  const [caseFileSortReverse, setCaseFileSortReverse] = React.useState(false);
  const [filePreviewSortBy, setFilePreviewSortBy] = React.useState(
    SortByType.Name
  );
  const [sceneDataList, setSceneDataList] = React.useState([]);
  const [showAdditionalName, setShowAdditionalName] = React.useState(true);
  const [showComments, setShowComments] = React.useState(true);
  const [filePreviewDialogOpen, setFilePreviewDialogOpen] =
    React.useState(false);

  const activeFileId = React.useMemo(() => {
    // return the id of the scene that has the newest lastOpenedAt date
    if (!sceneDataList || sceneDataList.length === 0) return null;
    const sorted = [...sceneDataList].sort((a, b) => {
      return new Date(b.lastOpenedAt) - new Date(a.lastOpenedAt);
    });
    return sorted[0]?.id || null;
  }, [sceneDataList]);

  const [layout, setLayout] = React.useState({
    dockbox: {
      mode: "horizontal",
      children: [
        {
          tabs: [{ id: "slideFolderTab" }],
        },
        {
          tabs: [{ id: "caseInformationTab" }],
        },
      ],
    },
  });

  React.useEffect(() => {
    if (!selCase || !selCase.Files || selCase.Files.length === 0) return;
    const sortedList = FileFormatUtils.sortEntitiesByTypeAndName(
      sceneDataList,
      filePreviewSortBy,
      false,
      caseFileSortReverse
    );
    setSceneDataList(sortedList);
  }, [filePreviewSortBy, caseFileSortReverse]);

  React.useEffect(() => {
    if (selCase.Files && selCase.Files.length === 0) return;
    const getScenesForFile = async (pathList) => {
      let list = await FileFormatUtils.getScenesForFile(pathList);

      // sort by scene.path
      list = FileFormatUtils.sortEntitiesByTypeAndName(
        list,
        filePreviewSortBy,
        false,
        caseFileSortReverse
      );
      setSceneDataList(list);
    };

    getScenesForFile(selCase.Files);
  }, [selCase.Files]);

  /**
   * Triggers a re-render of the layout by updating the dock key state.
   */
  const rerenderLayout = () => {
    setDockKey(dockKey + 1);
  };

  /**
   * Handles the event when the folder menu is opened.
   *
   * @param {Object} event - The event object triggered by the user action.
   */
  const onOpenFolderMenu = (event) => {
    setAnchorElFolder(event.currentTarget);
  };

  const onOpenPreviewMenu = (event) => {
    setAnchorElPreview(event.currentTarget);
  };

  React.useEffect(() => {
    rerenderLayout();
  }, [selCase]);

  let groups = {
    custom: {
      floatable: false,
      maximizable: false,
    },
  };

  /**
   * An object representing the slide folder tab configuration.
   *
   * @type {Object}
   * @property {JSX.Element} title - The title element of the tab, which includes a folder label and an icon button for opening a menu.
   * @property {JSX.Element} content - The content element of the tab, which renders the `SlideFolder` component with context values.
   * @property {string} group - The group to which this tab belongs, set to "custom".
   */
  let slideFolderTab = {
    title: (
      <div className={classes.previewTabTitle}>
        <div>PREVIEW</div>
        <IconButton onClick={onOpenFolderMenu}>
          <HSAIcon icon="SortIcon" size={28} />
        </IconButton>
        <IconButton onClick={onOpenPreviewMenu}>
          <HSAIcon icon="MoreVertIcon" size={28} />
        </IconButton>
      </div>
    ),
    content: <FilePreviewList />,

    group: "custom",
  };

  let caseInformationTab = {
    title: (
      <div className={classes.previewTabTitle}>
        <div>CASE INFORMATION</div>
        <span></span>
      </div>
    ),
    content: <CaseInformation />,
    group: "custom",
  };

  const loadTab = (data) => {
    let { id } = data;
    switch (id) {
      case "slideFolderTab":
        return { ...slideFolderTab, id };
      case "caseInformationTab":
        return { ...caseInformationTab, id };
    }
  };

  const onLayoutChange = (newLayout) => {
    let newChilds = newLayout.dockbox.children;
    let oldChilds = layout.dockbox.children;
    if (JSON.stringify(newChilds) !== JSON.stringify(oldChilds)) {
      setLayout(newLayout);
      // props.persistentStorage.save("SidebarLayout", newLayout);
    }
  };

  /**
   * Closes the menu by setting the anchor element for the folder to null.
   */
  const handleMenuClose = () => {
    setAnchorElFolder(null);
  };

  /**
   * Closes the preview menu by resetting the anchor element to null.
   * Typically used to hide or dismiss a popover or menu component.
   */
  const handlePreviewMenuClose = () => {
    setAnchorElPreview(null);
  };

  /**
   * Handles the sorting logic for file previews in the case table sidebar.
   * If the selected sort option is the same as the current one, toggles the sort order.
   * Otherwise, updates the sort option to the newly selected one.
   * Closes the menu and triggers a layout re-render after sorting.
   *
   * @param {string} sortBy - The field by which to sort the file previews.
   */
  const onChangeFileSortBy = (sortBy) => {
    if (filePreviewSortBy === sortBy) {
      setCaseFileSortReverse(!caseFileSortReverse);
    } else {
      setFilePreviewSortBy(sortBy);
    }
    handleMenuClose();
    rerenderLayout();
  };

  /**
   * Toggles the visibility of the additional name field in the UI.
   * Updates the `showAdditionalName` state to its opposite value.
   */
  const toggleAdditionalName = () => {
    setShowAdditionalName(!showAdditionalName);
  };

  /**
   * Toggles the visibility of the comments section in the UI.
   * Updates the `showComments` state to its opposite value.
   */
  const toggleShowComments = () => {
    setShowComments(!showComments);
  };

  /**
   * Opens the file preview and edit dialog.
   * Sets the dialog state to open and closes the preview menu.
   */
  const openFilePreviewEditDialog = () => {
    setFilePreviewDialogOpen(true);
    handlePreviewMenuClose();
  };

  return (
    <CaseContext.Provider
      value={{
        activeFileId,
        filePreviewSortBy,
        filePreviewSortReverse: caseFileSortReverse,
        pathList: [],
        sceneDataList,
        selCase,
        setActiveFileId: () => {},
        setActiveFilePath: () => {},
        setPathList: [],
        setSceneDataList,
        setShowAdditionalName,
        setShowComments,
        showAdditionalName,
        showComments,
        splitscreenFileIds: [],
        width: props.width,
      }}
    >
      <div className={classes.root} style={{ width: props.width }}>
        <DockLayout
          key={dockKey}
          layout={layout}
          loadTab={loadTab}
          onLayoutChange={onLayoutChange}
          groups={groups}
          style={{
            position: "relative",
            height: "100%",
          }}
        />

        <FilePreviewEditDialog
          open={filePreviewDialogOpen}
          onClose={() => setFilePreviewDialogOpen(false)}
        />

        {/* Sort menu for cases preview tab */}
        <Menu
          anchorEl={anchorElFolder}
          open={Boolean(anchorElFolder)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => onChangeFileSortBy(SortByType.Name)}>
            <span>Name</span>
            {filePreviewSortBy === SortByType.Name && (
              <span>
                {caseFileSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
          <MenuItem onClick={() => onChangeFileSortBy(SortByType.Date)}>
            <span>Creation Date</span>
            {filePreviewSortBy === SortByType.Date && (
              <span>
                {caseFileSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>

          <MenuItem onClick={() => onChangeFileSortBy(SortByType.Type)}>
            <span>File Type</span>
            {filePreviewSortBy === SortByType.Type && (
              <span>
                {caseFileSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={anchorElPreview}
          open={Boolean(anchorElPreview)}
          onClose={handlePreviewMenuClose}
        >
          <MenuItem onClick={openFilePreviewEditDialog}>
            <HSAIcon icon="Edit" size={28} style={{ marginRight: 8 }} />
            <span>Edit Preview</span>
          </MenuItem>
          <MenuItem onClick={toggleAdditionalName}>
            <Checkbox
              size="small"
              checked={showAdditionalName ?? true}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Show Additional Name</span>
          </MenuItem>
          <MenuItem onClick={toggleShowComments}>
            <Checkbox
              size="small"
              checked={showComments ?? true}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Show Comments</span>
          </MenuItem>
        </Menu>
      </div>
    </CaseContext.Provider>
  );
}

CaseTableDockSideBar.propTypes = {
  width: PropTypes.number.isRequired,
  selCase: PropTypes.object,
};

const useStyles = makeStyles({
  root: {
    height: "100%",
    width: 1000,
    background: "#fff",
    overflow: "hidden",
  },
  sortMenuButton: {
    position: "absolute",
    top: 3,
    right: 0,
  },
  previewTabTitle: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    alignItems: "center",
    position: "relative",
    bottom: 6,
    height: 44,
  },
});
